/**
 * MessageActions — §7.1 Full Message Action Suite
 *
 * Action bar that appears on hover for each message.
 * Assistant messages: Copy, Regenerate, Continue, Rate Good, Rate Bad, Info
 * User messages: Edit, Copy
 */

import { useState } from 'react';
import {
  Copy, CheckCheck, RefreshCw, ArrowRight, ThumbsUp, ThumbsDown,
  Info, Pencil, Loader2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { stripThinkingTags } from './ThinkingBlock';

interface MessageActionsProps {
  role: 'user' | 'assistant';
  content: string;
  messageId: string;
  metadata?: {
    model?: string;
    totalTokens?: number;
    latencyMs?: number;
    ragSourceCount?: number;
    confidenceScore?: number;
    intent?: string;
    stageTimings?: Record<string, number>;
  };
  onRegenerate?: () => void;
  onContinue?: () => void;
  onEdit?: (newContent: string) => void;
  onRate?: (rating: 'positive' | 'negative', reason?: string) => void;
  isRegenerating?: boolean;
  /**
   * bug #6 — server-hydrated feedback for this message. Used to restore the
   * thumb-up/down highlight after a reload (the component's own `rated` state
   * resets to null on every mount). When the user clicks again, the local
   * optimistic state takes precedence.
   */
  feedback?: { rating: 'positive' | 'negative'; reason?: string };
}

export function MessageActions({
  role,
  content,
  messageId,
  metadata,
  onRegenerate,
  onContinue,
  onEdit,
  onRate,
  isRegenerating,
  feedback,
}: MessageActionsProps) {
  const [copied, setCopied] = useState(false);
  const [rated, setRated] = useState<'positive' | 'negative' | null>(null);
  // bug #6 (feedback re-hydration) — the local `rated` state resets to null on
  // every mount, so a reloaded conversation lost the user's persisted rating.
  // Fall back to the server-hydrated `feedback.rating` until the user clicks
  // again, after which the local optimistic state takes precedence.
  const effectiveRated = rated ?? feedback?.rating ?? null;
  const [showInfo, setShowInfo] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editContent, setEditContent] = useState(content);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState('');

  const handleCopy = async () => {
    // bug #16 — give an explicit confirmation. The icon→check is subtle and
    // `clipboard.writeText` can reject silently (insecure context / no focus),
    // leaving the user with NO feedback. Toast on success, surface failures,
    // and fall back to a textarea copy when the async API is unavailable.
    const cleanContent = stripThinkingTags(content);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(cleanContent);
      } else {
        const ta = document.createElement('textarea');
        ta.value = cleanContent;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy — select the text and copy manually');
    }
  };

  const handleRate = (rating: 'positive' | 'negative') => {
    if (rating === 'negative') {
      setShowFeedback(true);
      return;
    }
    setRated(rating);
    onRate?.(rating);
  };

  const submitNegativeFeedback = () => {
    setRated('negative');
    onRate?.('negative', feedbackReason);
    setShowFeedback(false);
    setFeedbackReason('');
  };

  const handleEditSubmit = () => {
    onEdit?.(editContent);
    setShowEdit(false);
  };

  return (
    <>
      {/* Action bar — visibility controlled by parent .msg-actions class */}
      <div className="flex items-center gap-0.5">
        {/* Copy — both roles */}
        <ActionButton icon={copied ? CheckCheck : Copy} label={copied ? 'Copied' : 'Copy'} onClick={handleCopy} />

        {role === 'user' && (
          <ActionButton icon={Pencil} label="Edit" onClick={() => { setEditContent(content); setShowEdit(true); }} />
        )}

        {role === 'assistant' && (
          <>
            <ActionButton
              icon={isRegenerating ? Loader2 : RefreshCw}
              label="Regenerate"
              onClick={() => onRegenerate?.()}
              disabled={isRegenerating}
              className={isRegenerating ? 'animate-spin' : ''}
            />
            <ActionButton icon={ArrowRight} label="Continue" onClick={() => onContinue?.()} />
            <ActionButton
              icon={ThumbsUp}
              label="Good"
              onClick={() => handleRate('positive')}
              className={effectiveRated === 'positive' ? 'text-green-500' : ''}
            />
            <ActionButton
              icon={ThumbsDown}
              label="Bad"
              onClick={() => handleRate('negative')}
              className={effectiveRated === 'negative' ? 'text-red-500' : ''}
            />
            {metadata && (
              <ActionButton icon={Info} label="Info" onClick={() => setShowInfo(true)} />
            )}
          </>
        )}
      </div>

      {/* Info Dialog (§7.2) */}
      <Dialog open={showInfo} onOpenChange={setShowInfo}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Response Info</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            {metadata?.model && <InfoRow label="Model" value={metadata.model} />}
            {metadata?.intent && <InfoRow label="Intent" value={metadata.intent} />}
            {metadata?.totalTokens != null && <InfoRow label="Tokens" value={metadata.totalTokens.toLocaleString()} />}
            {metadata?.latencyMs != null && <InfoRow label="Latency" value={`${(metadata.latencyMs / 1000).toFixed(1)}s`} />}
            {metadata?.ragSourceCount != null && <InfoRow label="RAG Sources" value={String(metadata.ragSourceCount)} />}
            {metadata?.confidenceScore != null && (
              <InfoRow label="Confidence" value={
                <Badge variant={metadata.confidenceScore >= 70 ? 'default' : metadata.confidenceScore >= 40 ? 'secondary' : 'destructive'}>
                  {metadata.confidenceScore}
                </Badge>
              } />
            )}
            {metadata?.stageTimings && Object.keys(metadata.stageTimings).length > 0 && (
              <div className="pt-2 border-t">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Pipeline Stages</span>
                <div className="mt-1 space-y-1">
                  {Object.entries(metadata.stageTimings).map(([stage, ms]) => (
                    <div key={stage} className="flex justify-between text-xs">
                      <span className="text-muted-foreground font-mono">{stage}</span>
                      <span>{ms}ms</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Message</DialogTitle>
          </DialogHeader>
          <Textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            rows={4}
            className="resize-none"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
            <Button onClick={handleEditSubmit}>Send Edited</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Negative Feedback Dialog */}
      <Dialog open={showFeedback} onOpenChange={setShowFeedback}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>What went wrong?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {['Hallucination / made up data', 'Irrelevant response', 'Unsafe / inappropriate', 'Other'].map(reason => (
              <button
                key={reason}
                onClick={() => setFeedbackReason(reason)}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-md border text-sm transition-colors',
                  feedbackReason === reason ? 'border-primary bg-primary/10' : 'hover:bg-muted',
                )}
              >
                {reason}
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeedback(false)}>Cancel</Button>
            <Button onClick={submitNegativeFeedback} disabled={!feedbackReason}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function ActionButton({
  icon: Icon,
  label,
  onClick,
  disabled,
  className,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          disabled={disabled}
          // `label` is the visible tooltip copy ("Copy" / "Regenerate"
          // / "Good" / "Bad" / "Info" / "Edit" / "Continue"). Mirroring
          // it onto aria-label gives screen readers an accessible name
          // for an otherwise icon-only button — the dominant source of
          // axe `button-name` failures in Ask Argus per the deep a11y
          // audit (2026-05-12, 42 nodes on this rail alone).
          aria-label={label}
          title={label}
          className={cn(
            'p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50',
            className,
          )}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">{label}</TooltipContent>
    </Tooltip>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
